import React, { useState, useContext, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import api from '../services/api';
import PremiumSelect from '../components/PremiumSelect';
import './Login.css';

type TenantLoginOption = { slug: string; name: string };

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [tenantSlug, setTenantSlug] = useState<string>(() => {
    try {
      const raw = localStorage.getItem('lastTenantSlug') || 'home';
      return raw === 'default' ? 'home' : raw;
    } catch {
      return 'home';
    }
  });
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  /** `undefined` = carregando; `null` = usar campo texto; array = combobox */
  const [tenantChoices, setTenantChoices] = useState<TenantLoginOption[] | null | undefined>(
    undefined,
  );
  const authContext = useContext(AuthContext);
  const { login } = authContext || {};
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const state = location.state as {
      tenantSlug?: string;
      email?: string;
      registeredOrgName?: string;
    } | null;
    if (state?.tenantSlug) {
      setTenantSlug(state.tenantSlug);
    }
    if (state?.email) {
      setEmail(state.email);
    }
    if (state?.registeredOrgName) {
      setSuccessMessage(`Organização “${state.registeredOrgName}” criada. Faça login abaixo.`);
    }
  }, [location.state]);

  useEffect(() => {
    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      if (!cancelled) {
        setTenantChoices((current) => (current === undefined ? null : current));
        setError((current) =>
          current
            ? current
            : 'Não foi possível carregar as organizações. Verifique se a API está no ar e tente de novo.',
        );
      }
    }, 12000);

    (async () => {
      try {
        const { data } = await api.get<TenantLoginOption[]>('/tenants/login-options');
        if (cancelled) return;
        if (!data?.length) {
          setTenantChoices(null);
          return;
        }
        setTenantChoices(data);
        setTenantSlug((current) =>
          data.some((t) => t.slug === current) ? current : data[0].slug,
        );
      } catch {
        if (!cancelled) {
          setTenantChoices(null);
          setError(
            'Servidor indisponível. Confirme que o backend está rodando (porta 3061 no Docker) e atualize a página.',
          );
        }
      } finally {
        if (!cancelled) {
          window.clearTimeout(timeoutId);
        }
      }
    })();
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, []);

  const tenantPremiumOptions = useMemo(
    () =>
      (tenantChoices ?? []).map((t) => ({
        value: t.slug,
        label: `${t.name} (${t.slug})`,
      })),
    [tenantChoices],
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    if (tenantChoices === undefined) return;
    setLoading(true);

    try {
      if (login) {
        const result = await login(email, password, tenantSlug);
        navigate(result.user.role === 'developer' ? '/financeiro' : '/');
      }
    } catch (err: any) {
      const apiMessage = err.response?.data?.message;
      const message = Array.isArray(apiMessage)
        ? apiMessage.join(', ')
        : apiMessage ||
          (err.message === 'Network Error'
            ? 'Não foi possível conectar à API. Verifique se o backend está rodando.'
            : 'Erro ao fazer login');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-shell">
        <section className="login-showcase">
          <div className="login-showcase-badge">Plataforma financeira</div>
          <h1>Gestão financeira com mais clareza.</h1>
          <p>Centralize ciclos, devolutivas e evolução do time em um único lugar.</p>

          <div className="login-highlights">
            <div className="login-highlight-card">
              <strong>Ciclos organizados</strong>
              <span>Prazos e acompanhamento em um só fluxo.</span>
            </div>
            <div className="login-highlight-card">
              <strong>Avaliação padronizada</strong>
              <span>Mais consistência nas devolutivas.</span>
            </div>
            <div className="login-highlight-card">
              <strong>Pronto para qualquer tela</strong>
              <span>Desktop, tablet e mobile.</span>
            </div>
          </div>

          <div className="login-metrics" aria-hidden="true">
            <div>
              <strong>360</strong>
              <span>visão</span>
            </div>
            <div>
              <strong>100%</strong>
              <span>evolução</span>
            </div>
            <div>
              <strong>24/7</strong>
              <span>acesso</span>
            </div>
          </div>
        </section>

        <section className="login-panel">
          <div className="login-panel-header">
            <div className="login-logo" aria-hidden="true">
              <span>F</span>
            </div>
            <div>
              <p className="login-eyebrow">Financeiro</p>
              <h2>Acesse sua conta</h2>
            </div>
          </div>

          <p className="login-panel-copy">Entre para continuar.</p>

          <form className="login-form" onSubmit={handleSubmit}>
            <div className="login-field">
              {tenantChoices === undefined ? (
                <PremiumSelect
                  id="tenantSlug"
                  label="Organização"
                  wrapperClassName="premium-select-field login-tenant-premium"
                  value=""
                  options={[{ value: '', label: 'Carregando organizações…' }]}
                  onChange={() => {}}
                  disabled
                  placeholder="Carregando…"
                />
              ) : tenantChoices !== null ? (
                <PremiumSelect
                  id="tenantSlug"
                  label="Organização"
                  wrapperClassName="premium-select-field login-tenant-premium"
                  value={tenantSlug}
                  options={tenantPremiumOptions}
                  onChange={setTenantSlug}
                  placeholder="Selecione a organização"
                  required
                />
              ) : (
                <>
                  <label htmlFor="tenantSlug">Organização</label>
                  <div className="login-input-wrapper">
                    <span className="login-input-icon" aria-hidden="true">
                      /
                    </span>
                    <input
                      id="tenantSlug"
                      type="text"
                      value={tenantSlug}
                      onChange={(e) =>
                        setTenantSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                      }
                      placeholder="ex: minha-empresa"
                      autoComplete="organization"
                      required
                    />
                  </div>
                </>
              )}
            </div>

            <div className="login-field">
              <label htmlFor="email">Email corporativo</label>
              <div className="login-input-wrapper">
                <span className="login-input-icon" aria-hidden="true">
                  @
                </span>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu.email@empresa.com"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="password">Senha</label>
              <div className="login-input-wrapper">
                <span className="login-input-icon" aria-hidden="true">
                  *
                </span>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite sua senha"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
            </div>

            {successMessage && <div className="login-success">{successMessage}</div>}

            {error && <div className="login-error">{error}</div>}

            <button
              type="submit"
              className="login-submit"
              disabled={loading || tenantChoices === undefined}
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          <p className="login-register-footer">
            Primeira vez aqui?{' '}
            <Link to="/criar-organizacao" className="login-register-link">
              Criar organização
            </Link>
          </p>

          <div className="login-panel-footer">
            <span className="login-status-dot" aria-hidden="true" />
            Desktop, tablet e mobile
          </div>
        </section>
      </div>
    </div>
  );
};

export default Login;
